import { useAuth } from 'shared/providers/AuthProvider';
import { IconName } from '@televet/kibble-ui';
import { MutableRefObject, useMemo } from 'react';
import { SettingsRoutes } from 'routes';
import useClinicUser from 'shared/hooks/useClinicUser';
import { PermissionType } from 'shared/types/graphql';
import { useBillingInfo } from '../../../shared/hooks/useBillingInfo';
import ClinicHoursSettings from 'pages/Settings/pages/ClinicHoursSettings';
import BillingSettings from 'pages/Settings/pages/BillingSettings';
import AppointmentSettings from 'pages/Settings/pages/AppointmentSettings';
import AutomationsSettings from 'pages/Settings/pages/AutomationsSettings';
import PaymentSettings from 'pages/Settings/pages/PaymentSettings';
import WebsiteWidgetSettings from 'pages/Settings/pages/WebsiteWidgetSettings';
import ConversationSettings from 'pages/Settings/pages/ConversationSettings';
import DevToolsSettings from 'pages/Settings/pages/DevToolsSettings';
import GeneralInfoSettings, { CountryCode } from 'pages/Settings/pages/GeneralInfoSettings';
import MyNotificationsSettings from 'pages/Settings/pages/MyNotificationSettings';
import TextMessagingSettings from 'pages/Settings/pages/TextMessagingSettings';
import ServiceRemindersSettings from 'pages/Settings/pages/ServiceReminderSettings';
import TeamSettings from 'pages/Settings/pages/TeamSettings';
import MyProfileSettings from '../pages/MyProfileSettings/index';
import OttoLabsSettings from 'pages/Settings/pages/OttoLabsSettings';
import SyncStatusSettings from '../pages/SyncStatusSettings';
import MyPreferencesSettings from '../pages/MyPreferencesSettings';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { FeatureFlagName } from 'shared/enums/FeatureFlagName';
import useFeatureFlag from '../../../shared/hooks/useFeatureFlag';
import GlobalAutomationsSettings from '../pages/GlobalAutomationsSettings';
import { useFailedWritebackAppointments } from 'shared/hooks/useFailedWritebackAppointments';
import { ModuleSetting, useModularity } from 'shared/hooks/useModularity';
import SubscriptionSettings from '../pages/SubscriptionSettings';
import TaskSettings from '../pages/TaskSettings';

export type LeavingCurrentPage = {
  onLeavingCurrentPage: () => void;
  onClickedSaveButton: () => void;
  onClickedCancelButton: () => void;
};

interface PageDetails {
  label: string;
  relativePath: SettingsRoutes;
  leftIcon: IconName;
  rightIcon?: IconName;
  component:
    | React.ForwardRefExoticComponent<
        {
          hasPermission?: boolean;
          isSettingsChangesValid?: boolean;
          setHasUnsavedChange: React.Dispatch<React.SetStateAction<boolean>>;
          setIsSettingsChangesValid: React.Dispatch<React.SetStateAction<boolean>>;
        } & React.RefAttributes<unknown>
      >
    | (({
        hasPermission,
        viewRef,
      }: {
        hasPermission: boolean;
        viewRef: MutableRefObject<HTMLDivElement | null>;
      }) => JSX.Element | null);
  permission?: PermissionType;
  isVisible: boolean;
  id: string;
}

enum ISectionName {
  MY_SETTINGS = 'MY SETTINGS',
  CLINIC_SETTINGS = 'CLINIC SETTINGS',
  FLOW_SETTINGS = 'FLOW SETTINGS',
  INTERNAL_SETTINGS = 'INTERNAL SETTINGS',
  OTTO_LABS = 'OTTO LABS',
}

export const useSettingPageDetail = (): {
  settingsSection: {
    sectionName: ISectionName;
    sectionOption: PageDetails[];
  }[];
  allSectionsOptions: PageDetails[];
} => {
  const { hasAdminRoleAtCurrentClinic, isOttoTeamMember, clinicUser, isOttoGlobalAdmin } = useClinicUser();
  const { isImpersonating } = useAuth();
  const { isFeatureEnabled } = useFeatureFlag();
  const { displayBilling } = useBillingInfo();
  const {
    syncStatus: { hasSyncOutage },
  } = useIntegrationsProvider();
  const { showAiRecapWritebackNotification } = useFailedWritebackAppointments();
  const { modules, shouldSettingDisplayWithModules } = useModularity();
  const displayInternalSettings = useMemo(() => {
    return isOttoTeamMember || isImpersonating;
  }, [isImpersonating, isOttoTeamMember]);

  const hasTerminalApp = useMemo(() => {
    return isFeatureEnabled(FeatureFlagName.TerminalPaymentsApp);
  }, [isFeatureEnabled]);

  const isUSClinic = useMemo(() => {
    if (!clinicUser?.vetInfo?.currentClinic?.country) return true;
    return clinicUser?.vetInfo?.currentClinic?.country === CountryCode.US;
  }, [clinicUser?.vetInfo?.currentClinic?.country]);

  const mySettingsSection: PageDetails[] = [
    {
      label: 'Profile',
      relativePath: SettingsRoutes.UserProfile,
      leftIcon: 'personCircle',
      component: MyProfileSettings,
      isVisible: shouldSettingDisplayWithModules(ModuleSetting.Profile, modules),
      id: 'my-profile-settings-button',
    },
    {
      label: 'Notifications',
      relativePath: SettingsRoutes.UserNotifications,
      leftIcon: 'bell',
      component: MyNotificationsSettings,
      isVisible: shouldSettingDisplayWithModules(ModuleSetting.Notifications, modules),
      id: 'my-notifications-settings-button',
    },
    {
      label: 'Preferences',
      relativePath: SettingsRoutes.UserPreferences,
      leftIcon: 'sliders',
      component: MyPreferencesSettings,
      isVisible: shouldSettingDisplayWithModules(ModuleSetting.Preferences, modules),
      id: 'my-notifications-settings-button',
    },
  ];

  const clinicSettingsSection: PageDetails[] = useMemo(
    () => [
      {
        label: 'General Info',
        relativePath: SettingsRoutes.ClinicGeneral,
        leftIcon: 'house',
        component: GeneralInfoSettings,
        isVisible: shouldSettingDisplayWithModules(ModuleSetting.GeneralInfo, modules),
        id: 'general-info-settings-button',
      },
      {
        label: 'Clinic Hours',
        relativePath: SettingsRoutes.ClinicHours,
        leftIcon: 'clock',
        component: ClinicHoursSettings,
        isVisible: shouldSettingDisplayWithModules(ModuleSetting.ClinicHours, modules),
        id: 'clinic-hours-settings-button',
      },
      {
        label: 'Team',
        relativePath: SettingsRoutes.ClinicTeam,
        leftIcon: 'team',
        component: TeamSettings,
        isVisible: shouldSettingDisplayWithModules(ModuleSetting.Team, modules),
        id: 'team-settings-button',
      },
      {
        label: 'Subscription',
        relativePath: SettingsRoutes.ClinicSubscription,
        leftIcon: 'magic',
        component: SubscriptionSettings,
        isVisible: shouldSettingDisplayWithModules(ModuleSetting.Subscription, modules),
        id: 'subscription-settings-button',
      },
    ],
    [shouldSettingDisplayWithModules, modules],
  );

  const flowSettingsSection: PageDetails[] = useMemo((): PageDetails[] => {
    return [
      {
        label: 'Conversations',
        relativePath: SettingsRoutes.ClinicConversations,
        leftIcon: 'messages',
        component: ConversationSettings,
        isVisible: shouldSettingDisplayWithModules(ModuleSetting.Conversations, modules),
        id: 'conversations-settings-button',
      },
      {
        label: 'Tasks',
        relativePath: SettingsRoutes.Tasks,
        leftIcon: 'taskList',
        component: TaskSettings,
        isVisible: shouldSettingDisplayWithModules(ModuleSetting.Tasks, modules),
        id: 'tasks-settings-button',
      },
      {
        label: 'Website Widget',
        relativePath: SettingsRoutes.ClinicWidget,
        leftIcon: 'website',
        component: WebsiteWidgetSettings,
        isVisible: shouldSettingDisplayWithModules(ModuleSetting.WebsiteWidget, modules),
        id: 'website-widget-settings-button',
      },
      {
        label: 'Text Messaging',
        relativePath: SettingsRoutes.ClinicPhone,
        leftIcon: 'cellphone',
        component: TextMessagingSettings,
        isVisible: shouldSettingDisplayWithModules(ModuleSetting.TextMessaging, modules),
        id: 'text-messaging-settings-button',
      },
      {
        label: 'Appointments',
        relativePath: SettingsRoutes.Appointments,
        leftIcon: 'calendarDate',
        rightIcon: showAiRecapWritebackNotification ? 'warningSign' : undefined,
        component: AppointmentSettings,
        isVisible: shouldSettingDisplayWithModules(ModuleSetting.Appointments, modules),
        id: 'appointments-settings-button',
      },
      {
        label: 'Automations',
        relativePath: SettingsRoutes.ClinicAutomations,
        leftIcon: 'robot',
        component: AutomationsSettings,
        isVisible: shouldSettingDisplayWithModules(ModuleSetting.Automations, modules),
        id: 'automations-settings-button',
      },
      {
        label: 'Service Reminders',
        relativePath: SettingsRoutes.ClinicReminders,
        leftIcon: 'notification',
        component: ServiceRemindersSettings,
        isVisible: shouldSettingDisplayWithModules(ModuleSetting.ServiceReminders, modules),
        id: 'service-reminders-settings-button',
      },
      {
        label: 'Payments',
        relativePath: SettingsRoutes.ClinicPayment,
        leftIcon: 'invoice',
        component: PaymentSettings,
        isVisible:
          shouldSettingDisplayWithModules(ModuleSetting.Payments, modules) &&
          (hasAdminRoleAtCurrentClinic || hasTerminalApp) &&
          isUSClinic,
        id: 'payment-settings-button',
      },
      {
        label: 'Billing',
        relativePath: SettingsRoutes.ClinicBilling,
        leftIcon: 'logo',
        component: BillingSettings,
        isVisible: shouldSettingDisplayWithModules(ModuleSetting.Billing, modules) && displayBilling,
        id: 'billing-settings-button',
      },
      {
        label: 'Sync Status',
        relativePath: SettingsRoutes.SyncStatus,
        leftIcon: 'doubleArrowCircle',
        component: SyncStatusSettings,
        isVisible: shouldSettingDisplayWithModules(ModuleSetting.SyncStatus, modules),
        id: 'sync-status-settings-button',
        ...(hasSyncOutage && { rightIcon: 'alertCircle' }),
      },
    ];
  }, [
    shouldSettingDisplayWithModules,
    modules,
    showAiRecapWritebackNotification,
    hasAdminRoleAtCurrentClinic,
    hasTerminalApp,
    isUSClinic,
    displayBilling,
    hasSyncOutage,
  ]);

  const internalSettingsSection: PageDetails[] = useMemo((): PageDetails[] => {
    return [
      {
        label: 'Dev Tools',
        relativePath: SettingsRoutes.DevTools,
        leftIcon: 'code',
        component: DevToolsSettings,
        isVisible: displayInternalSettings,
        id: 'dev-tools-settings-button',
      },
      {
        label: 'Global Automations',
        relativePath: SettingsRoutes.GlobalAutomations,
        leftIcon: 'robot',
        component: GlobalAutomationsSettings,
        isVisible: isOttoGlobalAdmin,
        id: 'global-automations-settings-button',
      },
    ];
  }, [displayInternalSettings, isOttoGlobalAdmin]);

  const ottoLabsSettingsSection: PageDetails[] = [
    {
      label: 'Otto Labs',
      relativePath: SettingsRoutes.OttoLabs,
      leftIcon: 'eyedropper',
      component: OttoLabsSettings,
      isVisible: shouldSettingDisplayWithModules(ModuleSetting.OttoLabs, modules),
      id: 'otto-labs-settings-button',
    },
  ];

  const settingsSection: { sectionName: ISectionName; sectionOption: PageDetails[] }[] = [
    { sectionName: ISectionName.MY_SETTINGS, sectionOption: mySettingsSection },
    { sectionName: ISectionName.CLINIC_SETTINGS, sectionOption: clinicSettingsSection },
    { sectionName: ISectionName.FLOW_SETTINGS, sectionOption: flowSettingsSection },
    { sectionName: ISectionName.OTTO_LABS, sectionOption: ottoLabsSettingsSection },
  ];

  const allSectionsOptions = [
    ...mySettingsSection,
    ...clinicSettingsSection,
    ...flowSettingsSection,
    ...ottoLabsSettingsSection,
  ];

  if (displayInternalSettings) {
    settingsSection.push({ sectionName: ISectionName.INTERNAL_SETTINGS, sectionOption: internalSettingsSection });
    allSectionsOptions.push(...internalSettingsSection);
  }

  return {
    settingsSection,
    allSectionsOptions,
  };
};
